import { Injectable, inject } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import { injectDashboardActivePageTier } from 'src/app/api/queries/view-preset.queries';

/**
 * The dashboard's last selected page (`ov-dashboard-page`), for
 * `DashboardPageCoordinatorService`. A service rather than hooks because the
 * coordinator is a root service; it owns the two toasts so the coordinator stays
 * free of presentation concerns.
 */
@Injectable({ providedIn: 'root' })
export class DashboardViewPresetService {
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly activePage = injectDashboardActivePageTier();

  /**
   * The saved page id, or `undefined` when there is none **or the load failed**.
   * Degrading to `undefined` sends the coordinator down its "no saved selection"
   * path (`fallbackToFirstPage`) rather than leaving the dashboard with no page
   * selected at all.
   */
  loadActivePageId(dashboardId: string): Promise<number | undefined> {
    return this.activePage.load(dashboardId).catch(() => {
      this.toast.showError(
        this.translate.instant(
          'dashboard-presentation-preview-mode.message.failed-to-load-view-preset',
        ),
      );

      return undefined;
    });
  }

  /** Fire-and-forget; the toast is the only failure signal. */
  saveActivePageId(dashboardId: string, pageId: number): void {
    void this.activePage.save(dashboardId, pageId).catch(() => {
      this.toast.showError(
        this.translate.instant(
          'dashboard-presentation-preview-mode.message.failed-to-save-view-preset',
        ),
      );
    });
  }
}
